import React, { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "react-toastify";
import { addAdminLeague, lookupAdminLeagues } from "./api/fetchAdminLeagues";

export default function AddLeagueModal({ open, onClose, onAdded }) {
  const [query, setQuery] = useState("");
  const [apiId, setApiId] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setApiId("");
      setResults([]);
      setSelected(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const data = await lookupAdminLeagues(q);
        setResults(data?.items || []);
      } catch (err) {
        setResults([]);
        toast.error(err.response?.data?.error || "Search failed");
      } finally {
        setSearching(false);
      }
    }, 350);

    return () => window.clearTimeout(timer);
  }, [query, open]);

  const handleSubmit = async () => {
    const id = selected?.apiId || parseInt(apiId, 10);
    if (!Number.isFinite(id)) {
      toast.warn("Select a league or enter a valid API-Sports id");
      return;
    }

    setSubmitting(true);
    try {
      const data = await addAdminLeague({ apiId: id, isEnabled: true });
      toast.success(`${data.league?.name || "League"} added`);
      onAdded(data.league);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to add league");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="alg-modal-backdrop" onClick={onClose} role="presentation">
      <div className="alg-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="alg-modal-head">
          <strong>Add league from API-Sports</strong>
          <button type="button" className="am-chrome-btn am-chrome-btn-icon" onClick={onClose}>
            <X size={14} />
          </button>
        </div>
        <div className="alg-modal-body">
          <label className="alg-field-label">Search by name</label>
          <input
            className="alg-input"
            type="search"
            placeholder="e.g. Premier League, World Cup…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(null);
            }}
          />

          {searching ? (
            <p className="alg-modal-hint">
              <Loader2 size={14} className="am-spin" /> Searching…
            </p>
          ) : null}

          {results.length > 0 ? (
            <div className="alg-lookup-list">
              {results.map((row) => (
                <button
                  key={row.apiId}
                  type="button"
                  className={`alg-lookup-item${selected?.apiId === row.apiId ? " is-selected" : ""}`}
                  onClick={() => {
                    setSelected(row);
                    setApiId(String(row.apiId));
                  }}
                >
                  {row.logo ? <img src={row.logo} alt="" className="alg-lookup-logo" /> : null}
                  <span className="min-w-0 flex-1">
                    <strong className="block truncate">{row.name}</strong>
                    <span className="alg-lookup-meta">
                      {row.country || "—"} · ID {row.apiId}
                      {row.currentSeasonYear ? ` · ${row.currentSeasonYear}` : ""}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          <label className="alg-field-label">Or enter API-Sports league ID</label>
          <input
            className="alg-input"
            type="number"
            placeholder="e.g. 39"
            value={apiId}
            onChange={(e) => {
              setApiId(e.target.value);
              setSelected(null);
            }}
          />
        </div>
        <div className="alg-modal-foot">
          <button type="button" className="alg-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="alg-btn alg-btn-accent" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Adding…" : "Add & enable"}
          </button>
        </div>
      </div>
    </div>
  );
}
